import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TabsContent } from '../ui/tabs';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Activity, Brain, Calendar, CheckCircle, ClipboardCheck, Clock, Copy, ExternalLink, Link, Loader2, Mail, MapPin, Phone, Plus, RefreshCw, Send, Sparkles, Trash2, Video, MessageSquare, FileText, Briefcase, CornerDownLeft } from 'lucide-react';
import { toast } from 'sonner';
import { brainDocProcessorAPI } from '../../lib/api';
import { parseUTC } from '../../lib/dateUtils';
import { isToday, isYesterday } from 'date-fns';
import LinkedInThreadView from '../LinkedInThreadView';
import WhatsAppThreadView from '../WhatsAppThreadView';
import WhatsAppLeadSummary from './WhatsAppLeadSummary';
import { CallLogCard } from '../CallLogCard';
import { useCurrency } from '../../contexts/CurrencyContext';

const LeadActivityContent = React.memo(function LeadActivityContent({
  lead,
  notes,
  tasks,
  followups,
  emails,
  calls,
  meetings,
  deals,
  visits,
  activityTab,
  commentText,
  docVerification,
  docFollowupLoading,
  docLoading,
  setActivityTab,
  setNoteDialogOpen,
  setTaskDialogOpen,
  setFollowupDialogOpen,
  setEmailDialogOpen,
  setCallDialogOpen,
  setMeetingDialogOpen,
  setVisitDialogOpen,
  setSendWhatsAppOpen,
  setDocLoading,
  setDocFollowupLoading,
  setDocVerification,
  setCommentText,
  handleDeleteNote,
  handleAddComment,
  handleToggleTask,
  handleTaskStatusChange,
  handleDeleteTask,
  handleFollowUpStatusChange,
  handleRescheduleFollowUp,
  handleDeleteFollowUp,
  handleAddFollowUpSummary,
  handleDeleteEmail,
  handleCreateDeal,
  handleViewDeal,
  formatLocal,
  formatLocalDate,
  formatLocalTime,
  API_URL,
  token,
  noteComments,
  getCreatorName,
  allActivities,
  id,
  mergedCalls,
  scheduledMeetings,
  highlightedCallId,
}) {
  const navigate = useNavigate();
  const currency = useCurrency();
  return (
    <>
                {/* Add Button - below tabs, right aligned */}
                {activityTab !== 'all' && activityTab !== 'docs' && activityTab !== 'whatsapp' && activityTab !== 'linkedin' && (
                  <div className="flex justify-end mb-4">
                    <Button 
                      size="sm" 
                      onClick={() => {
                        if (activityTab === 'notes') setNoteDialogOpen(true);
                        else if (activityTab === 'tasks') setTaskDialogOpen(true);
                        else if (activityTab === 'followups') setFollowupDialogOpen(true);
                        else if (activityTab === 'emails') setEmailDialogOpen(true);
                        else if (activityTab === 'calls') setCallDialogOpen(true);
                        else if (activityTab === 'meetings') setMeetingDialogOpen(true);
                        else if (activityTab === 'deals') handleCreateDeal();
                        else if (activityTab === 'visits') setVisitDialogOpen(true);
                      }}
                      data-testid={`add-${activityTab}-btn`}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add {activityTab === 'notes' ? 'Note' : activityTab === 'tasks' ? 'Task' : activityTab === 'followups' ? 'Follow-up' : activityTab === 'emails' ? 'Email' : activityTab === 'calls' ? 'Call' : activityTab === 'meetings' ? 'Meeting' : activityTab === 'visits' ? 'Visit' : 'Deal'}
                    </Button>
                  </div>
                )}

                {/* All Activities - Grouped by Date */}
                <TabsContent value="all" className="space-y-2 mt-0">
                  {allActivities.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No activities yet</p>
                  ) : (
                    (() => {
                      const groups = { today: [], yesterday: [], earlier: [] };
                      allActivities.forEach(activity => {
                        const d = parseUTC(activity.created_at || activity.createdAt || 0);
                        if (isToday(d)) groups.today.push(activity);
                        else if (isYesterday(d)) groups.yesterday.push(activity);
                        else groups.earlier.push(activity);
                      });
                      const renderGroup = (label, items) => items.length === 0 ? null : (
                        <div key={label}>
                          <div className="flex items-center gap-2 mb-2 mt-3 first:mt-0">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-[10px] text-muted-foreground">{items.length}</span>
                          </div>
                          <div className="space-y-3">
                            {items.map((activity, index) => {
                              const isAICallActivity = activity.type === 'AI Call';
                              const isAIGenerated = activity.ai_generated === true;
                              const isCall = activity.type === 'Call' || activity.type === 'AI Call';
                              const tabMap = { 'Note': 'notes', 'Task': 'tasks', 'Follow-up': 'followups', 'Email': 'emails', 'Call': 'calls', 'AI Call': 'calls', 'Meeting': 'meetings', 'Visit': 'visits', 'WhatsApp': 'emails', 'Deal': 'deals' };
                              const targetTab = tabMap[activity.type] || 'all';
                              // Calls render the full auditable card (▶ play recording +
                              // collapsible transcript/summary) inline in the timeline.
                              if (isCall) {
                                return (
                                  <CallLogCard
                                    key={`${label}-call-${activity.id || index}`}
                                    call={activity}
                                    highlighted={highlightedCallId === activity.id}
                                    getCreatorName={getCreatorName}
                                  />
                                );
                              }
                              return (
                                <div
                                  key={`${label}-${index}`}
                                  className={`border border-border rounded-lg px-5 py-3.5 bg-card cursor-pointer hover:bg-muted/50 transition-colors ${isAICallActivity || isAIGenerated ? 'border-l-4 border-l-violet-400 dark:border-l-violet-500' : ''}`}
                                  onClick={() => setActivityTab(targetTab)}
                                  data-testid={`activity-item-${label}-${index}`}
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-medium text-sm">{activity.type}</p>
                                        {(isAICallActivity || isAIGenerated) && (
                                          <Badge className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700 border border-violet-200 hover:bg-violet-100 dark:bg-violet-900/40 dark:text-violet-400 dark:border-violet-800 dark:hover:bg-violet-900/40">
                                            AI Generated
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">
                                        {activity.summary || activity.text || activity.title || activity.subject || activity.notes || activity.purpose}
                                      </p>
                                    </div>
                                    <span className="text-xs text-muted-foreground shrink-0 ml-4">
                                      {isAICallActivity || isAIGenerated ? 'AI Brain' : getCreatorName(activity)} • {(activity.created_at || activity.createdAt) && formatLocalTime(activity.created_at || activity.createdAt)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                      return (
                        <div className="space-y-1">
                          {renderGroup('Today', groups.today)}
                          {renderGroup('Yesterday', groups.yesterday)}
                          {renderGroup('Earlier', groups.earlier)}
                        </div>
                      );
                    })()
                  )}
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="space-y-3">
                  {notes.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No notes yet. Click "Add Note" to create one.</p>
                  ) : (
                    notes.map((note) => (
                    <div key={note.id} className="border border-border rounded-md bg-card" data-testid={`note-card-${note.id}`}>
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm">{note.text}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {getCreatorName(note)} • {(note.created_at || note.createdAt) && formatLocal(note.created_at || note.createdAt)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteNote(note.id)}
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Comments Section */}
                      <div className="border-t border-border">
                        {(noteComments[note.id] || []).length > 0 && (
                          <div className="px-4 pt-3 pb-1 space-y-2">
                            {(noteComments[note.id] || []).map((comment) => (
                              <div key={comment.id} className="flex items-start gap-2 pl-3 border-l-2 border-muted-foreground/20" data-testid={`note-comment-${comment.id}`}>
                                <div className="flex-1">
                                  <p className="text-xs text-foreground">{comment.text}</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">
                                    {comment.created_by_name || 'User'} • {comment.created_at && formatLocal(comment.created_at, 'MMM dd, HH:mm')}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 px-4 py-2">
                          <Input
                            placeholder="Add a comment..."
                            value={commentText[note.id] || ''}
                            onChange={(e) => setCommentText(prev => ({ ...prev, [note.id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(note.id); } }}
                            className="h-8 text-xs"
                            data-testid={`note-comment-input-${note.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 shrink-0"
                            onClick={() => handleAddComment(note.id)}
                            data-testid={`note-comment-send-${note.id}`}
                          >
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                  )}
                </TabsContent>

                {/* Tasks Tab */}
                <TabsContent value="tasks" className="space-y-3">
                  {tasks.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No tasks yet. Click "Add Task" to create one.</p>
                  ) : (
                    tasks.map((task) => (
                    <div key={task.id} className={`border border-border rounded-md p-4 bg-card ${task.ai_generated ? 'border-l-4 border-l-violet-400' : ''}`}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={task.completed || task.status === 'completed'}
                            onChange={() => handleToggleTask(task.id)}
                            className="mt-1"
                            data-testid={`task-checkbox-${task.id}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className={`font-medium text-sm ${(task.completed || task.status === 'completed') ? 'line-through text-muted-foreground' : ''}`}>
                                    {task.title}
                                  </p>
                                  {task.ai_generated && (
                                    <Badge className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700 border border-violet-200 dark:bg-violet-900/40 dark:text-violet-400 dark:border-violet-800">
                                      AI Brain
                                    </Badge>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  {(task.due_date || task.dueDate) && (
                                    <Badge variant="outline" className="text-xs">
                                      Due: {formatLocal(task.due_date || task.dueDate, 'MMM dd')}
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs">
                                    {task.priority}
                                  </Badge>
                                  <Select 
                                    value={task.status || 'pending'} 
                                    onValueChange={(value) => handleTaskStatusChange(task.id, value)}
                                  >
                                    <SelectTrigger className="w-[120px] h-7 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">Pending</SelectItem>
                                      <SelectItem value="completed">Completed</SelectItem>
                                      <SelectItem value="missed">Missed</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  Created by {getCreatorName(task)} • {(task.created_at || task.createdAt) && formatLocal(task.created_at || task.createdAt)}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                onClick={() => handleDeleteTask(task.id)}
                                data-testid={`delete-task-${task.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                    </div>
                  ))
                  )}
                </TabsContent>

                {/* Follow-ups Tab */}
                <TabsContent value="followups" className="space-y-3">
                  {followups.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No follow-ups yet. Click "Add Follow-up" to create one.</p>
                  ) : (
                    followups.map((followup) => (
                    <div key={followup.id} className={`border border-border rounded-md p-4 bg-card ${followup.ai_generated ? 'border-l-4 border-l-violet-400' : ''}`}>
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-sm">{followup.date && formatLocal(followup.date, 'MMMM dd, yyyy')}</p>
                                {followup.ai_generated && (
                                  <Badge className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700 border border-violet-200 dark:bg-violet-900/40 dark:text-violet-400 dark:border-violet-800">
                                    AI Brain
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{followup.notes}</p>
                              {followup.summary && (
                                <p className="text-sm text-muted-foreground mt-2 italic">Summary: {followup.summary}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                Created by {getCreatorName(followup)} • {(followup.created_at || followup.createdAt) && formatLocal(followup.created_at || followup.createdAt)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Select 
                                value={followup.status || 'pending'} 
                                onValueChange={(value) => handleFollowUpStatusChange(followup.id, value)}
                              >
                                <SelectTrigger className="w-[120px] h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="missed">Missed</SelectItem>
                                </SelectContent>
                              </Select>
                              {(followup.status === 'pending' || !followup.status) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600"
                                  onClick={() => handleDeleteFollowUp(followup.id)}
                                  data-testid={`delete-followup-${followup.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {(followup.status === 'completed' || followup.status === 'missed') && !followup.summary && (
                            <div className="space-y-2">
                              <Textarea
                                placeholder="Add summary..."
                                rows={2}
                                data-testid={`followup-summary-${followup.id}`}
                                onBlur={(e) => handleAddFollowUpSummary(followup.id, e.target.value)}
                              />
                            </div>
                          )}
                          {(followup.status === 'pending' || !followup.status) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRescheduleFollowUp(followup.id)}
                              data-testid={`reschedule-followup-${followup.id}`}
                            >
                              <Calendar className="h-4 w-4 mr-2" />
                              Reschedule
                            </Button>
                          )}
                        </div>
                    </div>
                  ))
                  )}
                </TabsContent>

                {/* Emails Tab */}
                <TabsContent value="emails" className="space-y-3">
                  {emails.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No emails yet. Click "Add Email" to compose one.</p>
                  ) : (
                    (() => {
                    const renderMsg = (email) => {
                      const isInbound = (email.direction || 'outbound') === 'inbound';
                      const senderName = lead?.name || email.from_name || 'Lead';
                      return (
                      <div key={email.id} data-testid={`email-row-${email.id}`} className={`border rounded-md p-4 space-y-3 ${isInbound ? 'border-border border-l-4 border-l-emerald-500 bg-emerald-50/40 dark:bg-emerald-900/10' : email.ai_generated ? 'border-border border-l-4 border-l-violet-400 bg-card' : 'border-border bg-card'}`}>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {isInbound && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-800" data-testid={`email-inbound-badge-${email.id}`}>
                                <CornerDownLeft className="h-3 w-3 mr-1" />Reply received
                              </Badge>
                            )}
                            <p className="font-medium text-sm">{email.subject}</p>
                            {email.ai_generated && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700 border border-violet-200 dark:bg-violet-900/40 dark:text-violet-400 dark:border-violet-800">
                                AI Draft
                              </Badge>
                            )}
                            {!isInbound && email.status && email.status !== 'sent' && (
                              <Badge variant="outline" className="text-[10px]">{email.status}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{email.body}</p>
                          {email.reply && (
                            <div className="mt-3 pl-4 border-l-2 border-primary">
                              <p className="text-xs font-semibold text-primary mb-1">Reply:</p>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{email.reply}</p>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-3" data-testid={`email-meta-${email.id}`}>
                            {isInbound ? (
                              <><CornerDownLeft className="h-3 w-3 inline mr-1 text-emerald-600" />Received from {senderName}</>
                            ) : (
                              <>Sent by {getCreatorName(email)}</>
                            )} • {(email.sent_at || email.sentAt || email.created_at) && formatLocal(email.sent_at || email.sentAt || email.created_at)}
                          </p>
                        </div>
                        {!isInbound && !email.has_reply && !email.reply && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteEmail(email.id)}
                            data-testid={`delete-email-${email.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        )}
                      </div>
                      );
                    };
                    // Group messages into conversations by thread_id (Gmail-style).
                    const tmap = new Map();
                    (emails || []).forEach((e) => {
                      const key = e.thread_id || e.id;
                      if (!tmap.has(key)) tmap.set(key, []);
                      tmap.get(key).push(e);
                    });
                    const groups = Array.from(tmap.entries()).map(([key, msgs]) => {
                      const sorted = [...msgs].sort((a, b) => new Date(a.sent_at || a.created_at || 0) - new Date(b.sent_at || b.created_at || 0));
                      return { key, messages: sorted, root: sorted[0], latest: sorted[sorted.length - 1] };
                    });
                    groups.sort((a, b) => new Date(b.latest.sent_at || b.latest.created_at || 0) - new Date(a.latest.sent_at || a.latest.created_at || 0));
                    return groups.map((g) => {
                      if (g.messages.length === 1) return renderMsg(g.messages[0]);
                      const hasInbound = g.messages.some((m) => (m.direction || 'outbound') === 'inbound');
                      return (
                        <details key={g.key} open className="border border-border rounded-md bg-card group" data-testid={`email-thread-${g.key}`}>
                          <summary className="cursor-pointer select-none p-4 flex items-center justify-between gap-3 list-none [&::-webkit-details-marker]:hidden">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                {hasInbound && <CornerDownLeft className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                                <p className="font-medium text-sm truncate">{g.root.subject || '(no subject)'}</p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{g.messages.length} messages · last {formatLocal(g.latest.sent_at || g.latest.created_at)}</p>
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0">{g.messages.length}</Badge>
                          </summary>
                          <div className="px-4 pb-4 pt-3 space-y-3 border-t border-border">
                            {g.messages.map(renderMsg)}
                          </div>
                        </details>
                      );
                    });
                    })()
                  )}
                </TabsContent>

                {/* Calls Tab */}
                <TabsContent value="calls" className="space-y-3">
                  {mergedCalls.length === 0 ? (
                    <div className="text-center py-12" data-testid="calls-empty-state">
                      <Phone className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                      <p className="text-sm font-medium text-muted-foreground">No calls yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Click "Add Call" to log one, or use AI Brain Call from the left panel.</p>
                    </div>
                  ) : (
                    mergedCalls.map((call) => (
                      <CallLogCard
                        key={call.id}
                        call={call}
                        highlighted={highlightedCallId === call.id}
                        getCreatorName={getCreatorName}
                      />
                    ))
                  )}
                </TabsContent>

                {/* Meetings Tab */}
                <TabsContent value="meetings" className="space-y-3">
                  {/* Auto-Scheduled Meetings from AI Calls */}
                  {scheduledMeetings.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                        Auto-Scheduled from AI Calls
                      </h4>
                      {scheduledMeetings.map((sm) => (
                        <div key={sm.id} className="border border-violet-500/30 rounded-md p-4 bg-violet-500/5" data-testid={`scheduled-meeting-${sm.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs bg-violet-500/10 text-violet-400 border-violet-500/30">
                                  {sm.meeting_type_name || sm.meeting_type || 'Demo'}
                                </Badge>
                                <p className="font-medium text-sm">
                                  {sm.start_time && formatLocal(sm.start_time, "MMMM dd, yyyy 'at' h:mm a")}
                                </p>
                              </div>
                              {sm.notes && (
                                <p className="text-xs text-muted-foreground mt-1">{sm.notes}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                {sm.calendar_event_id && (
                                  <Badge variant="outline" className="text-xs">
                                    <Calendar className="h-3 w-3 mr-1" /> Google Calendar
                                  </Badge>
                                )}
                                {sm.confirmation_email_sent && (
                                  <Badge variant="outline" className="text-xs">
                                    <Mail className="h-3 w-3 mr-1" /> Email Sent
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Manual Meetings */}
                  {meetings.length === 0 && scheduledMeetings.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No meetings yet. Click "Add Meeting" to schedule one.</p>
                  ) : (
                    meetings.map((meeting) => (
                    <div key={meeting.id} className="border border-border rounded-md p-4 bg-card" data-testid={`meeting-card-${meeting.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm">
                                {meeting.date && formatLocal(meeting.date, 'MMMM dd, yyyy')}
                                {meeting.time && ` at ${meeting.time}`}
                              </p>
                              {meeting.duration && (
                                <Badge variant="outline" className="text-xs">{meeting.duration} min</Badge>
                              )}
                              {meeting.status && (
                                <Badge variant={meeting.status === 'scheduled' ? 'default' : 'secondary'} className="text-xs capitalize">
                                  {meeting.status}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <Video className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {meeting.platform === 'google_meet' && 'Google Meet'}
                                {meeting.platform === 'zoom' && 'Zoom'}
                                {meeting.platform === 'teams' && 'Microsoft Teams'}
                                {meeting.platform === 'face_to_face' && 'In Person'}
                                {meeting.platform === 'phone' && 'Phone Call'}
                                {meeting.platform === 'other' && 'Other'}
                                {!meeting.platform && (meeting.location || 'Virtual')}
                              </span>
                              {meeting.meeting_link && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => {
                                      navigator.clipboard.writeText(meeting.meeting_link);
                                      toast.success('Meeting link copied to clipboard');
                                    }}
                                    data-testid={`copy-meeting-link-${meeting.id}`}
                                  >
                                    <Copy className="h-3 w-3 mr-1" />
                                    Copy Link
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => window.open(meeting.meeting_link, '_blank')}
                                    data-testid={`open-meeting-link-${meeting.id}`}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Join
                                  </Button>
                                </div>
                              )}
                            </div>
                            {meeting.agenda && (
                              <p className="text-sm text-muted-foreground mt-1">{meeting.agenda}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Created by {getCreatorName(meeting)} • {(meeting.created_at || meeting.createdAt) && formatLocal(meeting.created_at || meeting.createdAt)}
                            </p>
                          </div>
                          <Badge variant="outline">{meeting.status}</Badge>
                        </div>
                    </div>
                  ))
                  )}
                </TabsContent>

                {/* Deals Tab */}
                <TabsContent value="deals" className="space-y-3">
                  {deals.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No deals yet. Click "Add Deal" to create one.</p>
                  ) : (
                    <div className="space-y-2">
                      {deals.map((deal) => (
                        <div key={deal.id} className="border border-border rounded-md p-4 bg-card cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleViewDeal(deal)}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold">{deal.deal_name}</p>
                                  <Badge variant="outline">{deal.stage}</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {(deal.line_items?.length || 0) + (deal.items?.length || 0)} items
                                </p>
                                <p className="text-xs text-muted-foreground mt-2">
                                  Created by {getCreatorName(deal)} • {deal.created_at && formatLocalDate(deal.created_at)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-primary">{currency.format(parseFloat(deal.total || 0))}</p>
                              </div>
                            </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Visits Tab */}
                <TabsContent value="visits" className="space-y-3">
                  {visits.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No visits yet. Click "Add Visit" to schedule one.</p>
                  ) : (
                    visits.map((visit) => (
                    <div key={visit.id} className="border border-border rounded-md p-4 bg-card" data-testid={`visit-card-${visit.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{visit.date && formatLocal(visit.date, 'MMMM dd, yyyy')}</p>
                            {visit.location && (
                              <div className="flex items-center gap-1 mt-1">
                                <MapPin className="h-3 w-3 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">{visit.location}</p>
                              </div>
                            )}
                            {visit.purpose && (
                              <p className="text-sm text-muted-foreground mt-1">{visit.purpose}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Created by {getCreatorName(visit)} • {(visit.created_at || visit.createdAt) && formatLocal(visit.created_at || visit.createdAt)}
                            </p>
                          </div>
                        </div>
                    </div>
                  ))
                  )}
                </TabsContent>

                {/* Document Tracker Tab */}
                <TabsContent value="docs" className="space-y-3" data-testid="doc-tracker-content">
                  {!docVerification?.has_requirements ? (
                    <div className="text-center py-12">
                      <ClipboardCheck className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                      <p className="text-sm font-medium text-muted-foreground">No document requirements set</p>
                      <p className="text-xs text-muted-foreground mt-1">Go to Doc Verification to apply a template for this lead</p>
                      <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/doc-verification')} data-testid="go-doc-verification-btn">
                        <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> Open Doc Verification
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Progress summary */}
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-card">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">{docVerification.summary?.industry || 'Custom'}</p>
                            <p className="text-sm font-semibold">{docVerification.summary?.received || 0} / {docVerification.summary?.total || 0} received</p>
                          </div>
                          <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${docVerification.summary?.completion_pct || 0}%` }} />
                          </div>
                          <Badge variant={docVerification.summary?.completion_pct === 100 ? 'default' : 'secondary'} className={`text-xs ${docVerification.summary?.completion_pct === 100 ? 'bg-emerald-600' : ''}`}>
                            {docVerification.summary?.completion_pct || 0}%
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                            setDocLoading(true);
                            brainDocProcessorAPI.getVerificationSummary(id).then(r => setDocVerification(r.data)).finally(() => setDocLoading(false));
                          }} data-testid="refresh-docs-btn">
                            {docLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          </Button>
                          {docVerification.summary?.pending > 0 && (
                            <Button size="sm" className="h-7 text-xs gap-1" disabled={docFollowupLoading} onClick={async () => {
                              setDocFollowupLoading(true);
                              try {
                                const r = await brainDocProcessorAPI.triggerFollowup(id);
                                if (r.data?.status === 'all_complete') toast.success('All docs received!');
                                else toast.success(`Follow-up draft created: "${r.data?.subject}"`);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              finally { setDocFollowupLoading(false); }
                            }} data-testid="lead-doc-followup-btn">
                              {docFollowupLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Sparkles className="h-3 w-3" /> AI Follow-up</>}
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Requirements list */}
                      <div className="border rounded-lg divide-y">
                        {docVerification.requirements?.map((req, i) => (
                          <div key={i} className="flex items-center justify-between py-2.5 px-3 hover:bg-muted/20" data-testid={`lead-doc-req-${req.name}`}>
                            <div className="flex items-center gap-2.5 min-w-0">
                              {req.status === 'received' ? <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" /> : <Clock className="h-4 w-4 text-amber-500 shrink-0" />}
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{req.name}</p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  {req.required ? <Badge variant="outline" className="text-[10px]">Required</Badge> : <Badge variant="outline" className="text-[10px] opacity-50">Optional</Badge>}
                                  {req.verified_by === 'brain_auto' && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600"><Brain className="h-2.5 w-2.5 mr-0.5" />AI</Badge>}
                                  {req.reminder_count > 0 && <span className="text-[10px] text-muted-foreground">{req.reminder_count} reminder(s)</span>}
                                </div>
                              </div>
                            </div>
                            <Badge variant="outline" className={`text-xs capitalize ${req.status === 'received' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                              {req.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                      {/* Inbound emails */}
                      {docVerification.inbound_emails?.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Inbound Emails</h4>
                          {docVerification.inbound_emails.map(email => (
                            <div key={email.id} className="flex items-center justify-between p-2.5 border rounded-lg text-xs" data-testid={`lead-inbound-${email.id}`}>
                              <div className="flex items-center gap-2">
                                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-medium">{email.subject || '(no subject)'}</span>
                                <span className="text-muted-foreground">{email.attachment_count} file(s)</span>
                              </div>
                              <Badge variant={email.classification_status === 'completed' ? 'default' : 'secondary'} className={`text-[10px] ${email.classification_status === 'completed' ? 'bg-emerald-600' : ''}`}>
                                {email.classification_status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* WhatsApp Conversation Thread — inside Activity sub-tabs */}
                <TabsContent value="whatsapp" className="space-y-3">
                  <WhatsAppLeadSummary leadId={id} onSendTemplate={() => setSendWhatsAppOpen(true)} />
                  <WhatsAppThreadView
                    leadId={id}
                    leadName={lead?.name}
                    onSendMessage={() => setSendWhatsAppOpen(true)}
                  />
                </TabsContent>

                {/* LinkedIn Conversation Thread — inside Activity sub-tabs */}
                <TabsContent value="linkedin" className="space-y-3">
                  <LinkedInThreadView
                    leadId={lead?.id}
                    leadName={lead?.name}
                    linkedinUrl={lead?.linkedin_url}
                  />
                </TabsContent>
    </>
  );
});

export default LeadActivityContent;
